import { RefObject, useCallback, useEffect, useMemo, useRef } from "react";

import { originalPositionMs } from "utils/originalMedia";

/**
 * Лента едет за воспроизведением сама.
 *
 * Дорожки нарисованы по вертикали: время растёт вниз, реплика стоит на `start / zoomLevel`
 * пикселей от начала колонки. Пока лекция играет, звучащее место уходит вниз за край экрана, и
 * переводчик всё время подкручивает ленту рукой — на папке в пять минут это десятки прокруток,
 * и каждая отвлекает от того, ради чего он слушает.
 *
 * Три решения, из которых состоит поведение:
 *
 * 1. **Пишем `scrollTop` напрямую, без `behavior: "smooth"`.** Плавная прокрутка — это
 *    собственная анимация браузера длиной в сотни миллисекунд; запуская её каждый кадр, мы
 *    получаем не плавность, а дрожь: каждая следующая отменяет предыдущую. Шаг в один кадр и
 *    так меньше пикселя.
 *
 * 2. **Звучащее место держим на трети экрана сверху, а не по центру и не у края.** Переводчику
 *    нужно видеть, что будет дальше: снизу остаются две трети — те самые несколько реплик, к
 *    которым он готовится.
 *
 * 3. **Рука человека главнее.** Он крутит ленту и во время воспроизведения — посмотреть соседнюю
 *    реплику, сверить конец папки. Отличаем его прокрутку от своей по простому признаку: если
 *    `scrollTop` стал не тем, который мы записали, ленту двигали не мы. Тогда слежение молчит
 *    несколько секунд и возвращается само — просить человека что-то нажимать, чтобы вернуть
 *    поведение, которое он не выключал, нельзя.
 */

/** Доля высоты экрана, на которой держим звучащее место. */
export const FOLLOW_ANCHOR = 0.35;

/** Насколько молчим после того, как ленту тронули рукой, мс. */
export const FOLLOW_GRACE_MS = 6000;

/** Расхождение, ниже которого прокрутку не трогаем: браузер округляет `scrollTop` сам. */
const SETTLE_PX = 3;

interface Options {
    /** Контейнер с прокруткой — тот же, в котором лежат обе колонки. */
    container: RefObject<HTMLElement | null>;
    /** Миллисекунд в пикселе: `top` реплики это `start / zoomLevel`. */
    zoomLevel: number;
    /** Идёт ли воспроизведение. */
    playing: boolean;
    /** Включено ли слежение в настройках. */
    enabled: boolean;
    /** Часы воспроизведения; подменяются в тестах. */
    positionMs?: () => number | null;
    now?: () => number;
}

/**
 * Возвращает `pause()` — сказать слежению помолчать. Нужен там, где ленту двигает сам интерфейс
 * (переход к реплике из отчёта, восстановление прокрутки при открытии папки): такой прыжок
 * человек заказал, и возвращать его к звучащему месту в ту же секунду — значит отменить его
 * действие.
 */
export const useFollowPlayback = ({
    container,
    zoomLevel,
    playing,
    enabled,
    positionMs = originalPositionMs,
    now = () => Date.now(),
}: Options): { pause: () => void } => {
    // Что записали в прошлый раз — по нему и узнаём чужую прокрутку.
    const ourScrollRef = useRef<number | null>(null);
    const quietUntilRef = useRef(0);
    // Часы и положение держим в ссылках, а не в зависимостях: умолчания создают новую функцию
    // на каждый рендер, и цикл кадров пересоздавался бы вместе с ней — на каждое нажатие,
    // каждую правку, каждый тик соседнего таймера.
    const positionRef = useRef(positionMs);
    positionRef.current = positionMs;
    const nowRef = useRef(now);
    nowRef.current = now;

    const pause = useCallback(() => {
        quietUntilRef.current = nowRef.current() + FOLLOW_GRACE_MS;
        ourScrollRef.current = null;
    }, []);

    useEffect(() => {
        if (!enabled || !playing) {
            // Пауза не значит «человек ушёл»: следующий запуск начинает следить заново.
            ourScrollRef.current = null;
            return undefined;
        }
        let frame = 0;
        const step = () => {
            frame = window.requestAnimationFrame(step);
            const el = container.current;
            if (!el || !Number.isFinite(zoomLevel) || zoomLevel <= 0) return;

            const ours = ourScrollRef.current;
            if (ours !== null && Math.abs(el.scrollTop - ours) > SETTLE_PX) {
                // Ленту двигали не мы.
                quietUntilRef.current = nowRef.current() + FOLLOW_GRACE_MS;
                ourScrollRef.current = null;
                return;
            }
            if (nowRef.current() < quietUntilRef.current) return;

            const ms = positionRef.current();
            if (ms === null || !Number.isFinite(ms)) return;

            const limit = Math.max(0, el.scrollHeight - el.clientHeight);
            const target = Math.min(
                limit,
                Math.max(0, ms / zoomLevel - el.clientHeight * FOLLOW_ANCHOR),
            );
            if (Math.abs(el.scrollTop - target) <= SETTLE_PX) {
                // Уже там. Запоминаем фактическое значение, иначе округление браузера на
                // следующем кадре прочтётся как чужая прокрутка и слежение само себя выключит.
                ourScrollRef.current = el.scrollTop;
                return;
            }
            el.scrollTop = target;
            ourScrollRef.current = el.scrollTop;
        };
        frame = window.requestAnimationFrame(step);
        return () => window.cancelAnimationFrame(frame);
    }, [container, zoomLevel, playing, enabled]);

    return useMemo(() => ({ pause }), [pause]);
};

export default useFollowPlayback;
